import { RootState } from '@/store/store';
import { FilterFn } from '@tanstack/react-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchValue } from '@/store/slices/applicationSlice';
import { useSearchParams } from 'react-router-dom';
import { FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { setSearchByAssetType } from '@/store/slices/assetsPreviewSlice';
import { useAppFilters } from '@/hooks';

// Convert column header to readable label
const resolveHeaderLabel = (col: any): string => {
  if (typeof col.header === 'string') return col.header;
  if (React.isValidElement(col.header)) {
    const child = col.header.props?.children;
    if (typeof child === 'string') return child;
    if (Array.isArray(child)) return child.map((c) => (typeof c === 'string' ? c : '')).join(' ');
  }
  if (typeof col.header === 'function') return col.accessorKey;
  return col.accessorKey ?? 'unknown';
};

// Filter by selected field or fallback to global
const createFieldOrGlobalFilterFn = (
  fieldMap: Record<string, string>,
  selectedLabel: string | null,
  allSearchKeys: string[],
  columns: any[]
): FilterFn<any> => {
  const getCellValue = (accessorKey: string, row: any) => {
    const column = columns.find((col) => col.accessorKey === accessorKey || col.id === accessorKey);
    let cellValue: any;
    // If column has accessorFn, use it to get the value
    if (column?.accessorFn) {
      cellValue = column.accessorFn(row.original);
    } else {
      cellValue = row.getValue(accessorKey);
    }

    if (column?.cell && !column?.accessorFn) {
      const renderResult = column.cell({ getValue: () => cellValue, row });
      cellValue =
        typeof renderResult === 'string'
          ? renderResult
          : renderResult?.props?.children?.props?.children || renderResult?.props?.children || '';
    }

    return String(cellValue || '').toLowerCase();
  };

  return (row, columnId, filterValue) => {
    const lowercaseFilter = filterValue.toLowerCase();

    if (!selectedLabel || selectedLabel === 'All') {
      return allSearchKeys.some((accessorKey) => getCellValue(accessorKey, row).includes(lowercaseFilter));
    }

    const accessorKey = fieldMap[selectedLabel];
    if (!accessorKey) return true;

    return getCellValue(accessorKey, row).includes(lowercaseFilter);
  };
};

export const useSearchByHeader = (columns: any[], enableSearchStateSync = false) => {
  const dispatch = useDispatch();
  const { assetsType, assetsPreview } = useAppFilters();
  const { searchValue } = useSelector((state: RootState) => state.application);

  const searchByType = useSelector((state: RootState) =>
    assetsType ? state.assetsPreview.searchByAssetType?.[assetsType] : ''
  );
  const isAssetsPreviewEnableSearchSync = assetsPreview === 'assets' && enableSearchStateSync && assetsType;

  const [inputOnlyValue, setInputOnlyValue] = useState('');
  const [selectedHeaderLabel, setSelectedHeaderLabel] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAssetsPreviewEnableSearchSync) {
      const syncValue = searchByType || '';
      setInputOnlyValue(syncValue);
      dispatch(setSearchValue(syncValue));
    }
  }, [searchByType, isAssetsPreviewEnableSearchSync, dispatch]);

  const { fieldMap, fieldList, globalSearchKeys, hasGlobalSearch } = useMemo(() => {
    const map: Record<string, string> = {};
    const list: { label: string; accessorKey: string }[] = [];
    const allSearchableKeys: string[] = [];

    columns.forEach((col) => {
      if (col.enableSearch) {
        if (typeof col.accessorKey === 'string') {
          const label = resolveHeaderLabel(col);
          map[label] = col.accessorKey;
          list.push({ label, accessorKey: col.accessorKey });

          if (col.enableGlobalSearch) {
            allSearchableKeys.push(col.accessorKey);
          }
        }
      }
    });

    const includeAll = allSearchableKeys.length > 0;
    const finalList = includeAll ? [{ label: 'All', accessorKey: '' }, ...list] : list;
    const defaultLabel = includeAll ? 'All' : list[0]?.label ?? null;

    return {
      fieldMap: map,
      fieldList: finalList,
      globalSearchKeys: allSearchableKeys,
      hasGlobalSearch: includeAll,
      defaultLabel,
    };
  }, [columns]);

  useEffect(() => {
    if (!selectedHeaderLabel) {
      setSelectedHeaderLabel(hasGlobalSearch ? 'All' : fieldList[0]?.label ?? null);
    }
  }, [hasGlobalSearch, fieldList, selectedHeaderLabel]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputOnlyValue(value);
    if (isAssetsPreviewEnableSearchSync) {
      dispatch(setSearchValue(value));
      dispatch(setSearchByAssetType({ assetType: assetsType, search: value }));
    }
  };

  const handleSuggestionClick = (label: string) => {
    setSelectedHeaderLabel(label);
    setSuggestions([]);
    searchInputRef.current?.focus();
  };

  const handleClearInput = () => {
    setSuggestions([]);
    setInputOnlyValue('');
    setSelectedHeaderLabel(hasGlobalSearch ? 'All' : fieldList[0]?.label ?? null);
    if (isAssetsPreviewEnableSearchSync) {
      dispatch(setSearchValue(''));
      dispatch(setSearchByAssetType({ assetType: assetsType, search: '' }));
    }
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return {
    filterFn: createFieldOrGlobalFilterFn(fieldMap, selectedHeaderLabel, globalSearchKeys, columns),
    fieldList,
    selectedHeaderLabel,
    setSelectedHeaderLabel,
    inputOnlyValue,
    handleInputChange,
    handleSuggestionClick,
    handleClearInput,
    searchInputRef,
    suggestions,
    searchValue,
    hasGlobalSearch,
  };
};
